function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
var _excluded = ["className", "label", "labelClassName", "labelComponent", "labelId", "errorMessage", "errorMessageClassName", "errorPlacement", "hint", "requirementLabel", "inversed", "wrapperIsFieldset"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import InlineError from '../InlineError/InlineError';
import classNames from 'classnames';
import { errorPlacementDefault } from '../flags';
import useId from '../utilities/useId';

// TODO: Reimplement focusTrigger in another place, like another hook

// Some of the FormLabel's props we pass on to the component prop definitions.
// TODO: This could use a better name
/**
 * This is the set of public-facing props that each component that uses `useFormLabel`
 * can include in its own props definition.
 */
/**
 * This is the full list of props accepted by `useFormLabel`. Components that use
 * `useFormLabel` should not include this entire set in their props but instead use
 * `FormFieldProps`, which is the set of public-facing props that a component can
 * include in its own props.
 */
/**
 * This hook contains business logic for how an input field relates to its label
 * and wrapping container element. It takes a component's props and generates
 * the specialized set of props for its label, field, wrapper element, and
 * possible detached "bottom error". While the order and structure of those
 * elements are different between components and are specific to their
 * implementation, much of the logic for prop/attribute generation is consistent
 * between components. The hook will return the relevant information as
 * properties of an object:
 *
 *   - labelProps:   Props to be applied to a `FormLabel` (where we get the name)
 *   - fieldProps:   Props to be applied to the field (a.k.a., the input)
 *   - wrapperProps: Props to be applied to the wrapping element
 *   - bottomError:  A rendered React element representing a bottom-placed error
 *   - errorId:      The id (string) of the error-message element, in case we
 *                   need to reference it (currently only necessary to support
 *                   the FormControl component, which this hook aims to replace)
 *
 * Note that while it's helpful now to abstract away this logic behind a shared
 * hook, that may not always be the case. The first step in creating this hook
 * was to allow an alternative to `FormControl` that allowed for more freedom in
 * how the component's individual elements were structured at to get rid of the
 * dichotomy it imposes on components where their implementation must be split
 * between an inner and outer component.
 */
export function useFormLabel(props) {
  var _props$labelId, _props$errorId, _props$ariaInvalid;
  // TODO: Once we're on React 18, we can use the `useId` hook
  var id = useId('field--', props.id);
  var labelId = (_props$labelId = props.labelId) !== null && _props$labelId !== void 0 ? _props$labelId : "".concat(id, "__label");
  var errorId = (_props$errorId = props.errorId) !== null && _props$errorId !== void 0 ? _props$errorId : "".concat(id, "__error");
  var className = props.className,
    label = props.label,
    labelClassName = props.labelClassName,
    labelComponent = props.labelComponent,
    _labelId = props.labelId,
    errorMessage = props.errorMessage,
    errorMessageClassName = props.errorMessageClassName,
    _props$errorPlacement = props.errorPlacement,
    errorPlacement = _props$errorPlacement === void 0 ? errorPlacementDefault() : _props$errorPlacement,
    hint = props.hint,
    requirementLabel = props.requirementLabel,
    inversed = props.inversed,
    wrapperIsFieldset = props.wrapperIsFieldset,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var hasBottomError = errorPlacement === 'bottom' && errorMessage;
  var bottomError = hasBottomError ? /*#__PURE__*/React.createElement(InlineError, {
    id: errorId,
    inversed: inversed,
    className: classNames('ds-c-field__error-message--bottom', errorMessageClassName)
  }, errorMessage) : undefined;
  // Bottom placed errors cannot be linked to Choices in ChoiceList,
  // so we add a hidden error message to the label
  var showHiddenError = wrapperIsFieldset && bottomError;
  var ariaInvalid = (_props$ariaInvalid = props['aria-invalid']) !== null && _props$ariaInvalid !== void 0 ? _props$ariaInvalid : !!errorMessage;
  var labelProps = {
    children: /*#__PURE__*/React.createElement(React.Fragment, null, label, showHiddenError && /*#__PURE__*/React.createElement("div", {
      className: "ds-u-visibility--screen-reader"
    }, errorMessage)),
    className: labelClassName,
    component: labelComponent,
    errorMessage: bottomError ? undefined : errorMessage,
    errorMessageClassName: bottomError ? undefined : errorMessageClassName,
    errorId: errorId,
    // Avoid using `for` attribute for components with multiple inputs
    // i.e. ChoiceList, DateField, and other components that use `fieldset`
    fieldId: wrapperIsFieldset ? undefined : id,
    hint: hint,
    id: labelId,
    requirementLabel: requirementLabel,
    inversed: inversed
  };
  var fieldProps = _objectSpread(_objectSpread({}, remainingProps), {}, {
    id: id,
    errorMessage: errorMessage,
    inversed: inversed,
    'aria-describedby': hasBottomError ? classNames(props['aria-describedby'], errorId) : props['aria-describedby'],
    'aria-invalid': !wrapperIsFieldset ? ariaInvalid : undefined
  });
  var wrapperClassNames = classNames({
    'ds-c-fieldset': wrapperIsFieldset
  }, className);
  var wrapperProps = {
    className: wrapperClassNames,
    'aria-invalid': wrapperIsFieldset ? ariaInvalid : undefined
  };
  return {
    labelProps: labelProps,
    fieldProps: fieldProps,
    wrapperProps: wrapperProps,
    bottomError: bottomError,
    errorId: errorId
  };
}
export default useFormLabel;